import React, { useState, useRef } from "react";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import {
  Button,
  Row,
  Col,
  Space,
  Table,
  Tag,
  Image,
  Statistic,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import { getTablePagination } from "../../../utils/tablePagination";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionListTableScroll from "../../../hooks/useReceptionListTableScroll";
import {
  capitalizeFirstLetter,
  formatToINR,
  formatBedDays,
} from "../../../helpers/utility";
import bedIcon from "../../../assets/images/patientIcons/bed.svg";
import InPatientModal from "./InPatientModal";
import IpUploadModal from "./IpUploadModal";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { navigateToPatientIp } from "../../../helpers/urlFilters";

export const IP_ADMISSION_PAGE_SIZE = 20;

const IpPatientTable = ({
  data,
  refreshIpPage,
  page,
  setPage,
  loading,
  currentTab,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = useReceptionListTableScroll(
    tableWrapRef,
    isMobile,
    "inpatient"
  );
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState("consent");

  const columns = [
    { title: "Admission ID", key: "ip_id", dataIndex: "ip_id", width: 125 },
    {
      title: "Full Name",
      dataIndex: "patient_name",
      width: 300,
      sorter: (a, b) => a?.patient_name?.localeCompare(b?.patient_name),
      render: (_, record) => (
        <Row>
          <Col span={10}>{capitalizeFirstLetter(record?.patient_name)}</Col>

          <Col span={7}>
            <Tag>
              {(() => {
                const age = record?.patient_age_by_dob;
                const gender = record?.patient_gender;
                const genderShort =
                  gender === "Male"
                    ? "M"
                    : gender === "Female"
                    ? "F"
                    : gender === "Other"
                    ? "Other"
                    : "";
                let ageText = "";
                if (age != null) {
                  if (age < 1) {
                    const months = Math.round(age * 12);
                    ageText = `${months} months`;
                  } else {
                    ageText = `${Math.floor(age)}`;
                  }
                }
                if (ageText && genderShort)
                  return `${ageText} / ${genderShort}`;
                if (ageText) return ageText;
                if (genderShort) return genderShort;
                return "";
              })()}
            </Tag>
          </Col>

          <Col span={7}>
            {formatBedDays(record?.total_bed_days) && (
              <Tag color="blue">{formatBedDays(record?.total_bed_days)}</Tag>
            )}
          </Col>

          {/* <Col span={6}>
            {record?.is_active === true ? (
              <Tag color="green">
                {dayjs(record?.admission_date).format("DD MMM YYYY")}
              </Tag>
            ) : (
              <Tag>{dayjs(record?.discharge_date).format("DD MMM YYYY")}</Tag>
            )}
          </Col> */}
        </Row>
      ),
    },
    {
      title: "Consultant Name",
      dataIndex: "doctor_name",
      key: "doctor_name",
      width: 200,
      render: (value) => capitalizeFirstLetter(value) || "-",
    },
    ...(currentTab === "active"
      ? [
          {
            title: "Admission Date",
            key: "admission_date",
            dataIndex: "admission_date",
            width: 100,
            sorter: (a, b) =>
              dayjs(a.admission_date).unix() - dayjs(b.admission_date).unix(),
            render: (value, record) => (
              <>
                <Tag color="green">{dayjs(value).format("DD MMM YYYY")}</Tag>
              </>
            ),
          },
        ]
      : currentTab === "discharged"
      ? [
          {
            title: "Admission Date",
            key: "admission_date",
            dataIndex: "admission_date",
            width: 100,
            sorter: (a, b) =>
              dayjs(a.admission_date).unix() - dayjs(b.admission_date).unix(),
            render: (value, record) => (
              <>
                <Tag color="green">{dayjs(value).format("DD MMM YYYY")}</Tag>
              </>
            ),
          },
          {
            title: "Discharge Date",
            key: "discharge_date",
            dataIndex: "discharge_date",
            sorter: (a, b) =>
              dayjs(a.discharge_date).unix() - dayjs(b.discharge_date).unix(),
            width: 100,
            render: (value, record) => (
              <>
                <Tag>{dayjs(value).format("DD MMM YYYY")}</Tag>
              </>
            ),
          },
        ]
      : [
          {
            title: "Admission Date",
            key: "admission_date",
            dataIndex: "admission_date",
            sorter: (a, b) =>
              dayjs(a.admission_date).unix() - dayjs(b.admission_date).unix(),
            width: 100,
            render: (value, record) => (
              <>
                {value ? (
                  <Tag color="green">{dayjs(value).format("DD MMM YYYY")}</Tag>
                ) : (
                  "-"
                )}
              </>
            ),
          },

          {
            title: "Discharge Date",
            key: "discharge_date",
            dataIndex: "discharge_date",
            width: 100,
            sorter: (a, b) => {
              const dateA = a.discharge_date
                ? dayjs(a.discharge_date).unix()
                : Infinity;
              const dateB = b.discharge_date
                ? dayjs(b.discharge_date).unix()
                : Infinity;
              return dateA - dateB;
            },
            render: (value, record) => (
              <>
                {value ? <Tag>{dayjs(value).format("DD MMM YYYY")}</Tag> : "-"}
              </>
            ),
          },
        ]),
    {
      title: (
        <>
          Balance{" "}
          <>
            <Tooltip
              title="Balances refresh automatically every hour.
Use “Sync Balance” to update the amount instantly."
            >
              <InfoCircleOutlined />
            </Tooltip>
          </>
        </>
      ),
      key: "balance",
      dataIndex: "balance",
      width: 150,
      sorter: (a, b) => (a?.balance || 0) - (b?.balance || 0),

      render: (value, record) =>
        value * -1 < 0 ? (
          <Statistic
            value={formatToINR(value * -1)}
            // prefix="-"
            valueStyle={{ color: "#FD6050", fontSize: "16px" }}
          />
        ) : value * -1 > 0 ? (
          <Statistic
            value={formatToINR(value * -1)}
            prefix="+"
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
          />
        ) : (
          <Statistic
            value={formatToINR(value)}
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
          />
        ),
    },
    {
      title: "Bed Assignment",
      render: (value, record) => (
        <>
          {record?.bed_name ? (
            <Space>
              {/* <Tag color="blue" className={tagStyles.smRndTag}> */}
              <Tag color="orange">
                <Image src={bedIcon} preview={false} /> {record?.bed_name}
              </Tag>
            </Space>
          ) : (
            <Button
              disabled={!record?.is_active}
              onClick={() =>
                navigateToPatientIp(navigate, `/receptionist/ip?patient=${record?.id}&type=bed-management`)
              }
              danger
            >
              Allot Bed
            </Button>
          )}
        </>
      ),
      width: 120,
    },
    {
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            ghost
            onClick={() =>
              navigateToPatientIp(navigate, `/receptionist/ip?patient=${record?.id}&type=billing&status=${
                    record?.is_active ? "current" : "older"
                  }`)
            }
          >
            View
          </Button>
          {record?.is_active && (
            <ActionIconButton
              aria-label="Edit patient"
              onClick={() => {
                setOpen(true);
                setEditData(record);
              }}
            >
              <EditIcon />
            </ActionIconButton>
          )}
        </Space>
      ),
      width: 120,
    },
  ];

  // Adding rowClassName to differentiate rows without bed assignment

  return (
    <>
      <div ref={tableWrapRef} className="reception-patient-list-table">
        <Table
          className="no-vertical-borders"
          loading={loading}
          dataSource={data?.data}
          columns={columns}
          pagination={getTablePagination({
            current: page,
            pageSize: IP_ADMISSION_PAGE_SIZE,
            total: data?.total_count ?? 0,
            onChange: (p) => setPage(p),
          })}
          scroll={tableScroll}
        />
      </div>

      {open && (
        <InPatientModal
          open={open}
          setOpen={setOpen}
          ipData={editData}
          refreshIpPage={refreshIpPage}
        />
      )}
      {/* {openModal && (
        <IpUploadModal
          setOpenModal={setOpenModal}
          openModal={openModal}
          editData={editData}
          refreshIpPage={refreshIpPage}
          type={type}
        />
      )} */}
    </>
  );
};

export default IpPatientTable;
